import clsx from 'clsx'
import { AnimatePresence, motion } from 'motion/react'
import { useEffect, useRef, useState } from 'react'

import MemberOnboardingSupportPopover from 'components/MemberOnboardingSupportPopover'
import { getIntercomLauncherOffset } from 'components/reusable/intercom/IntercomLauncher'
import LoadingSpinnerCentered from 'components/reusable/LoadingSpinnerCentered'
import { Banner, TooltipProvider, useBreakpoint } from 'slab'
import { type MemberOnboardingEnrollmentEvent } from 'utils/web_api.schemas'
import { useWindowLocation } from 'utils/windowLocation'

import { AllowanceSidebar } from './components/AllowanceSidebar'
import { MobileNavBar } from './components/MobileNavBar'
import { Navigation } from './components/Navigation'
import { OnboardingGenericErrorMessage } from './components/OnboardingGenericErrorMessage'
import { ProfileMenu } from './components/ProfileMenu'
import { emitStepPageViewEvent } from './events'
import { getStoredCurrentStep, storeNavigation } from './navigationUtils'
import { OnboardingProvider, useOnboarding } from './OnboardingContext'
import {
  canShowThisStep,
  componentMappings,
  getBaseStep,
  getStepFromData,
  getStepMetadata,
} from './OnboardingSteps'
import { type OnboardingInfo, type Step } from './types'
import { isRenewal } from './utils'

const cleanUpStepParam = () => {
  const url = new URL(window.location.href)
  url.searchParams.delete('step')
  window.history.replaceState({}, '', url.toString())
}

function OnboardingOrchestratorImpl ({
  initialStep,
  isMemberOnTieredAllowance,
  userId,
  showHelpAndSupport = true,
}: {
  initialStep?: Step | null;
  isMemberOnTieredAllowance: boolean | null; // needed to render the allowance sidebar
  userId: string;
  showHelpAndSupport?: boolean;
}) {
  const { replace } = useWindowLocation()

  const {
    onboardingData,
    isLoading,
    error,
    refreshOnboardingInfo,
    refreshPlanChangeSummary,
    enrollmentEventId,
    showOnboardingUnderMaintenanceBanner,
    showSmartBudget,
  } = useOnboarding()
  const [currentStep, setCurrentStep] = useState<Step | null>(null)
  const [layout, setLayout] = useState<
    'full' | 'no_sidebar' | 'sidebar' | undefined
  >()

  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const { breakpointIsAtLeast } = useBreakpoint()

  // Based on the onboarding information, we determine if the given step
  // (stepToRender) is OK to render. If not, we determine the actual step
  // to render based on the onboarding information (via getStepFromData)
  const getValidStepOrFallback = (
    dataToCheck: OnboardingInfo,
    stepToRender: Step
  ): Step | null => {
    if (
      canShowThisStep({
        data: dataToCheck,
        step: stepToRender,
        featureFlags: { showSmartBudget: !!showSmartBudget },
      })
    ) {
      return stepToRender
    }
    return getStepFromData(dataToCheck)
  }

  // Store the current page as the navigation history & last visited page
  useEffect(() => {
    if (currentStep && enrollmentEventId) {
      storeNavigation(currentStep, enrollmentEventId)
      // if the current step should be rendering the page as full page, trigger the full page mode
      // otherwise put it back to non-full page mode
      setLayout(componentMappings[getBaseStep(currentStep)]?.layout)

      // Scroll to top when step changes
      const scrollingElement = scrollContainerRef.current || window
      scrollingElement.scrollTo(0, 0)
    }
  }, [currentStep, enrollmentEventId])

  useEffect(() => {
    if (onboardingData?.progress === 'completed') {
      replace('/member_onboarding/post_enrollment')
    }
  }, [onboardingData?.progress])

  useEffect(() => {
    if (currentStep && onboardingData) {
      // Emit page view event for the current step
      emitStepPageViewEvent(
        userId,
        currentStep,
        onboardingData,
        getStepMetadata(currentStep)
      )
    }
  }, [currentStep, onboardingData])

  // This hook will be called during the initial rendering,
  // when currentStep has not been set
  useEffect(() => {
    if (!onboardingData || currentStep) return

    let stepToRender: Step | null = null
    // First, see if there is an initial step that is supplied to this component
    // (in case the user is trying to access the page via ?step parameter)
    // If there is, see if that step is valid to render. If not, fall back to
    // getting the step from the onboarding data.
    if (initialStep) {
      stepToRender = getValidStepOrFallback(onboardingData, initialStep)
    } else {
      // If not, then see if there is a "last visited page" from the user's session storage
      // If there is, see if that step is valid to render. If not, fall back to
      // getting the step from the onboarding data.
      const lastVisitedPage = enrollmentEventId
        ? getStoredCurrentStep(enrollmentEventId)
        : null
      if (lastVisitedPage) {
        stepToRender = getValidStepOrFallback(
          onboardingData,
          lastVisitedPage as Step
        )
      } else {
        // If none of them exists, get the step from the onboarding data.
        stepToRender = getStepFromData(onboardingData)
      }
    }
    setCurrentStep(stepToRender)
  }, [onboardingData, currentStep, initialStep, enrollmentEventId])

  if (isLoading) {
    return (
      <div
        data-testid='OnboardingOrchestratorLoading'
        className='flex items-center justify-center h-[calc(100vh-400px)]'
      >
        <LoadingSpinnerCentered />
      </div>
    )
  }

  let errorComponent = null
  if (error) {
    const errorMessage = <OnboardingGenericErrorMessage error={error} />

    errorComponent = (
      <div className='mb-6'>
        <Banner
          title='A problem has occurred while processing your request'
          type='critical'
        >
          {errorMessage}
        </Banner>
      </div>
    )
  }

  const StepComponent = currentStep
    ? componentMappings[getBaseStep(currentStep)]?.component || null
    : null

  // Get the base step for the current step to avoid repetition
  const currentBaseStep = currentStep ? getBaseStep(currentStep) : null
  const currentStepMapping = currentBaseStep
    ? componentMappings[currentBaseStep]
    : null

  // Callback function used to navigate to the next step
  const navigateToNextStep = async (updatedData?: OnboardingInfo | null) => {
    if (currentStep === null) return
    cleanUpStepParam()
    const refreshedData = await refreshOnboardingInfo(updatedData)
    if (onboardingData && isRenewal(onboardingData)) {
      await refreshPlanChangeSummary()
    }
    const dataToCheck = updatedData || refreshedData!
    const nextStep = currentStepMapping?.nextStep(dataToCheck)
    if (!nextStep) return

    const actualStepToShow = getValidStepOrFallback(dataToCheck, nextStep)
    if (actualStepToShow) {
      setCurrentStep(actualStepToShow)
    }
  }

  const navigateToSpecificStep = async (step: Step) => {
    if (currentStep === null) return
    cleanUpStepParam()
    const refreshedData = await refreshOnboardingInfo()
    if (onboardingData && isRenewal(onboardingData)) {
      await refreshPlanChangeSummary()
    }
    const actualStepToShow = getValidStepOrFallback(refreshedData!, step)
    if (actualStepToShow) {
      setCurrentStep(actualStepToShow)
    }
  }

  const shouldShowPreviousStep =
    currentStep === null
      ? false
      : currentStepMapping?.previousStep(undefined, enrollmentEventId) !== null

  const navigateToPreviousStep = (data?: OnboardingInfo | null) => {
    if (currentStep === null) return
    cleanUpStepParam()
    const prevStep = currentStepMapping?.previousStep(data, enrollmentEventId)
    if (prevStep) {
      setCurrentStep(prevStep)
    }
  }

  const renderStepComponent = () => {
    if (StepComponent === null) {
      return null
    }

    return (
      <AnimatePresence>
        <motion.div
          key={currentStep}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <StepComponent
            navigateToPreviousStep={
              shouldShowPreviousStep ? navigateToPreviousStep : undefined
            }
            navigateToNextStep={navigateToNextStep}
            navigateToSpecificStep={navigateToSpecificStep}
            metadata={getStepMetadata(currentStep)}
            userId={userId}
          />
        </motion.div>
      </AnimatePresence>
    )
  }

  // The `full` layout works for all viewports
  if (layout === 'full') {
    if (errorComponent) {
      return (
        <div className='w-screen h-screen bg-gray-50 flex items-center justify-center p-4'>
          <div className='max-w-lg w-full'>{errorComponent}</div>
        </div>
      )
    }

    return renderStepComponent()
  }

  // For viewports at or above the `xs` breakpoint, show the three-column layout
  if (breakpointIsAtLeast('xs')) {
    return (
      <div className='flex fixed inset-0 overflow-x-auto overflow-y-hidden'>
        <aside className='flex flex-col flex-shrink-0 py-6 pl-6 pr-0 gap-y-10'>
          <div className='-ml-2'>
            <ProfileMenu
              showOptedOut={
                onboardingData
                  ? canShowThisStep({
                    data: onboardingData,
                    step: 'view_allowances',
                    featureFlags: { showSmartBudget: !!showSmartBudget },
                  })
                  : false
              }
              navigateToSpecificStep={navigateToSpecificStep}
            />
          </div>
          <div className='w-[13.125rem]'>
            <Navigation
              currentStep={currentStep}
              onClickStep={(step) => navigateToSpecificStep(step)}
            />
            <div id='plan-comparison-portal' className='mt-10' />
          </div>
          {showHelpAndSupport && (
            <div className='mt-auto'>
              <MemberOnboardingSupportPopover />
            </div>
          )}
        </aside>
        <main
          className='overflow-y-auto basis-[36rem] shrink-0 grow'
          ref={scrollContainerRef}
        >
          <div className='pt-24 pb-10 pr-10 xl:pr-16 pl-6'>
            {showOnboardingUnderMaintenanceBanner && (
              <div className='mb-4 max-w-[44rem]'>
                <Banner
                  title='Onboarding is experiencing issues'
                  type='critical'
                >
                  We're encountering some issues with enrollments. You can still
                  submit, but we may need to follow up with you for more
                  information later.
                </Banner>
              </div>
            )}
            {errorComponent ?? renderStepComponent()}
          </div>
        </main>
        {layout === 'sidebar' && (
          <div className='overflow-y-auto bg-gray-50 shadow-default relative z-10 shrink-1 min-w-[19rem] w-full max-w-[22.5rem]'>
            <AllowanceSidebar
              isAtFinalizeEnrollment={
                currentStep === 'finalize_enrollment' ||
                currentStep === 'renewal_confirm_enrollment'
              }
              navigateToSpecificStep={navigateToSpecificStep}
              isMemberOnTieredAllowance={!!isMemberOnTieredAllowance}
            />
          </div>
        )}
      </div>
    )
  }

  // Mobile layout
  return (
    <div className='isolate'>
      <MobileNavBar>
        <div className='-ml-2 min-w-0'>
          <ProfileMenu
            showHelpAndSupport
            showOptedOut={
              onboardingData
                ? canShowThisStep({
                  data: onboardingData,
                  step: 'view_allowances',
                  featureFlags: { showSmartBudget: !!showSmartBudget },
                })
                : false
            }
            navigateToSpecificStep={navigateToSpecificStep}
            width='full'
          />
        </div>
        <nav className='flex-shrink-0'>
          <Navigation
            currentStep={currentStep}
            onClickStep={(step) => navigateToSpecificStep(step)}
            displayInDropdown
          />
        </nav>
      </MobileNavBar>
      <main
        className={clsx('px-4 pt-4', {
          'pb-[6rem]': layout === 'sidebar',
          'pb-[2.5rem]': layout !== 'sidebar',
        })}
      >
        {showOnboardingUnderMaintenanceBanner && (
          <div className='mb-4'>
            <Banner title='Onboarding is experiencing issues' type='critical'>
              We're encountering some issues with enrollments. You can still
              submit, but we may need to follow up with you for more information
              later.
            </Banner>
          </div>
        )}
        {errorComponent ?? renderStepComponent()}
        {layout === 'sidebar' && (
          <AllowanceSidebar
            displayInBottomSheet
            isAtFinalizeEnrollment={
              currentStep === 'finalize_enrollment' ||
              currentStep === 'renewal_confirm_enrollment'
            }
            navigateToSpecificStep={navigateToSpecificStep}
            isMemberOnTieredAllowance={!!isMemberOnTieredAllowance}
          />
        )}
        <div
          id='plan-comparison-portal'
          className='fixed'
          style={getIntercomLauncherOffset('top')}
        />
      </main>
    </div>
  )
}

export default function OnboardingOrchestrator ({
  enrollmentEvent,
  employerName,
  step,
  mapboxAccessToken,
  isMemberOnTieredAllowance,
  isFerryVerificationTasksEnabled,
  userId,
  prefillDummyData,
  showHelpAndSupport = true,
  showOnboardingUnderMaintenanceBanner = false,
  showSmartBudget = false,
  qmeEnabledForHsaEligiblePlans = false,
  tuftsPlanUnavailableBanner = false,
}: {
  enrollmentEvent: MemberOnboardingEnrollmentEvent;
  employerName: string;
  step?: Step | null;
  mapboxAccessToken: string;
  userId: string;
  isMemberOnTieredAllowance: boolean | null;
  isFerryVerificationTasksEnabled?: boolean | null;
  prefillDummyData: boolean;
  showHelpAndSupport?: boolean;
  showOnboardingUnderMaintenanceBanner?: boolean | null;
  showSmartBudget?: boolean | null;
  qmeEnabledForHsaEligiblePlans?: boolean | null;
  tuftsPlanUnavailableBanner?: boolean | null;
}) {
  return (
    <OnboardingProvider
      enrollmentEventId={enrollmentEvent.id}
      employerName={employerName}
      mapboxAccessToken={mapboxAccessToken}
      prefillDummyData={prefillDummyData}
      isMemberOnTieredAllowance={isMemberOnTieredAllowance}
      showOnboardingUnderMaintenanceBanner={
        showOnboardingUnderMaintenanceBanner
      }
      isFerryVerificationTasksEnabled={isFerryVerificationTasksEnabled}
      showSmartBudget={showSmartBudget}
      qmeEnabledForHsaEligiblePlans={qmeEnabledForHsaEligiblePlans}
      tuftsPlanUnavailableBanner={tuftsPlanUnavailableBanner}
    >
      <TooltipProvider>
        <OnboardingOrchestratorImpl
          initialStep={step}
          isMemberOnTieredAllowance={isMemberOnTieredAllowance}
          userId={userId}
          showHelpAndSupport={showHelpAndSupport}
        />
      </TooltipProvider>
    </OnboardingProvider>
  )
}
